import React, { Component } from 'react';
import { find } from 'lodash';

import Select from '../../../elements/Select';
import withLocalization from '../../../hoc/withLocalization';

class ScrollSelectWidgetBase extends Component {
    constructor(props) {
        super(props);
        this.state = {
            rows: [],
            pages: 0,
            page: 0,
            loading: false,
            initted: false,
        };
        this.loadOptionsDebounced = null;
        this.currentInputValue = '';
    }

    componentDidMount() {
        this.handleLoadInitialOptions();
    }

    handleLoadInitialOptions = () => {
        this.currentInputValue = '';
        this.setState({ page: 0 });
        this.doLoad(undefined, 0, true);
    };

    componentWillUnmount() {
        if (this.loadOptionsDebounced) clearTimeout(this.loadOptionsDebounced);
    }

    componentDidUpdate(prevProps) {
        const { applyOnFirstLoad, value, onChange, isShiftEdit, cacheKey } = this.props;
        const { initted, rows, loading } = this.state;

        if (prevProps.cacheKey !== cacheKey) {
            this.handleLoadInitialOptions();
            return;
        }

        if (!loading && rows.length && applyOnFirstLoad && !initted) {
            const options = this.prepareOptions(rows);
            let row = find(options, o => `${o.value}` === `${value}`);
            if (!row && !isShiftEdit) row = options.length ? options[0] : null;
            this.setState({ initted: true });
            if (row) onChange(row.value, row);
        }
    }

    doLoad = async (search, page, replace) => {
        const { loadFn } = this.props;
        this.setState({ loading: true });
        try {
            const result = await loadFn(search, page);
            const newRows = Array.isArray(result) ? result : (result.rows || []);
            const pages = Array.isArray(result) ? 1 : (result.pages ?? 1);
            this.setState(prev => ({
                rows: replace ? newRows : [...prev.rows, ...newRows],
                pages,
                loading: false,
            }));
            return newRows;
        } catch (e) {
            this.setState({ loading: false });
            throw e;
        }
    };

    handleInputChange = (newValue, actionMeta) => {
        if (actionMeta.action === 'input-change') {
            this.currentInputValue = newValue;
            this.setState({ page: 0 });
            return newValue;
        }
        return newValue;
    };

    handleMenuScrollToBottom = async () => {
        const { page, pages } = this.state;
        const hasMore = page + 1 < pages;
        if (!hasMore) return;
        const nextPage = page + 1;
        await this.doLoad(this.currentInputValue || undefined, nextPage, false);
        this.setState({ page: nextPage });
    };

    loadOptions = (inputValue, callback) => {
        if (this.loadOptionsDebounced) clearTimeout(this.loadOptionsDebounced);
        this.currentInputValue = inputValue;
        this.loadOptionsDebounced = setTimeout(async () => {
            const rows = await this.doLoad(inputValue, 0, true);
            const preparedOptions = this.prepareOptions(rows);
            this.setState({ page: 0 });
            callback(preparedOptions);
        }, 300);
    };

    prepareOptions = (rows) => {
        const { prepareOption, allowAll, allLabel, t } = this.props;
        const options = (rows || []).map(prepareOption);
        if (allowAll) options.unshift({ value: '', label: t(allLabel || 'All') });
        return options;
    };

    render() {
        const {
            value, onChange, disabled, error,
            placeholder, t, menuPlacement, maxMenuHeight,
            isClearable = true, allowAll,
            loadFn, prepareOption, cacheKey, applyOnFirstLoad, allLabel, isShiftEdit,
            ...rest
        } = this.props;
        const { rows, loading } = this.state;

        const options = this.prepareOptions(rows);
        const currentSelect = find(options, o => {
            if (allowAll && !value) return String(o.value) === String('') || String(o.value) === String('all');
            if (value === 'all') return String(o.value) === String('') || String(o.value) === String('all');
            return String(o.value) === String(value);
        });

        return (
            <div className="managers-widget">
                <Select
                    asyncSelect
                    options={options}
                    defaultOptions={options}
                    cacheOptions={true}
                    error={error}
                    value={currentSelect || null}
                    isDisabled={disabled}
                    isClearable={isClearable}
                    placeholder={placeholder || t('Select')}
                    onChange={id => {
                        if (!id || !id.value) {
                            this.handleLoadInitialOptions();
                            onChange(null, null);
                        } else {
                            onChange(id.value, id);
                        }
                    }}
                    style={{ width: '100%' }}
                    onMenuScrollToBottom={this.handleMenuScrollToBottom}
                    loadOptions={this.loadOptions}
                    isLoading={loading}
                    onInputChange={this.handleInputChange}
                    menuPlacement={menuPlacement}
                    maxMenuHeight={maxMenuHeight}
                    {...rest}
                />
            </div>
        );
    }
}

export const ScrollSelectWidget = withLocalization(ScrollSelectWidgetBase);
